import { useState, useEffect } from 'react';
import { userSession } from '../utils/userSession.js';
import { useData } from '../context/DataContext.js';
import AddVenue from './AddVenue.js';
import AddLayout from './AddLayout.js';
import DashboardModal from './DashboardModal.js';
import FormTabs, { FormTabPanel } from './FormTabs.js';
import FormSearchSelect, { optionMetaCount } from './FormSearchSelect.js';
// State, venue, and layout use FormSearchSelect (not Combobox in this file).

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'location', label: 'Location' }
];

export default function AddEvent({ isOpen, onClose, onEventCreated }) {
  const { refreshEvents, states, venues, layouts, countries, refreshLayouts, refreshVenues } = useData();
  const [tab, setTab] = useState('details');
  const [formData, setFormData] = useState({
    country: 0,
    startDateTime: '',
    endDateTime: '',
    state: '',
    venue: '',
    layout: '',
    eventName: '',
    eventType: '',
    competitionType: '',
    roundNumber: '',
    eventLink: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [showAddLayout, setShowAddLayout] = useState(false);
  const [dateErrors, setDateErrors] = useState({
    start: '',
    end: ''
  });
  const [dateConstraints, setDateConstraints] = useState({ min: '', max: '' });

  // Fetch active seasons and set date constraints
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await fetch(
          'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/seasons'
        );
        const data = await response.json();
        
        if (Array.isArray(data)) {
          const today = new Date();
          const currentYear = today.getFullYear();
          
          // Filter out past seasons - only keep seasons where the year is >= current year
          // If it's the current year, we still allow it (events can be created for later in the year)
          const activeSeasons = data.filter(season => {
            if (season.year > currentYear) return true;
            if (season.year === currentYear) return true; // Allow current year
            return false; // Exclude past years
          });
          
          if (activeSeasons.length > 0) {
            // Get the earliest and latest years from active seasons
            const years = activeSeasons.map(s => s.year).sort((a, b) => a - b);
            const earliestYear = years[0];
            const latestYear = years[years.length - 1];
            
            // Set min to today (events can't be in the past)
            // But if today is before the earliest active season year, use Jan 1 of earliest year
            const minDate = today.getFullYear() < earliestYear 
              ? new Date(earliestYear, 0, 1, 0, 0)
              : today;
            
            // Set max to Dec 31 of latest year at 23:59
            const maxDate = new Date(latestYear, 11, 31, 23, 59);
            
            // Format for datetime-local input (YYYY-MM-DDTHH:mm)
            const formatForInput = (date) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              return `${year}-${month}-${day}T${hours}:${minutes}`;
            };
            
            setDateConstraints({
              min: formatForInput(minDate),
              max: formatForInput(maxDate)
            });
          }
        }
      } catch (error) {
        console.error('Error fetching seasons:', error);
        // If fetch fails, just use today as min (validation will catch invalid years)
        const today = new Date();
        const formatForInput = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        };
        setDateConstraints({
          min: formatForInput(today),
          max: '' // No max if we can't fetch seasons
        });
      }
    };
    
    if (isOpen) {
      fetchSeasons();
    }
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        country: countries?.[0]?.CountryID || 0,
        startDateTime: '',
        endDateTime: '',
        state: '',
        venue: '',
        layout: '',
        eventName: '',
        eventType: '',
        competitionType: '',
        roundNumber: '',
        eventLink: ''
      });
      setError('');
      setTab('details');
      setShowAddVenue(false);
      setShowAddLayout(false);
    }
  }, [isOpen, countries]);

  if (!isOpen) return null;
  // Get states for selected country
  const countryStates = states
    ?.filter(state => parseInt(state.CountryID) === parseInt(formData.country))
    || [];

  // Get venues for selected state
  const stateVenues = venues
    ?.filter(venue => parseInt(venue.StateID) === parseInt(formData.state))
    .map(venue => ({
      id: venue.VenueID,
      name: venue.VenueName
    })) || [];

  // Get layouts for selected venue from layouts context
  const venueLayouts = layouts
    ?.filter(layout => parseInt(layout.VenueID) === parseInt(formData.venue))
    .map(layout => ({
      id: layout.LayoutID,
      name: layout.LayoutName
    })) || [];

  // Get selected venue name for AddLayout component
  const selectedVenue = venues.find(v => parseInt(v.VenueID) === parseInt(formData.venue));

  const eventTypes = [
    'Beginner',
    'Practice',
    'Competition',
    'Exhibition'
  ];

  const validateDates = () => {
    const now = new Date();
    const start = new Date(formData.startDateTime);
    const end = new Date(formData.endDateTime);
    const errors = {
      start: '',
      end: ''
    };

    // Check if start date is in the past
    if (start < now) {
      errors.start = 'Start date/time cannot be in the past';
    }

    // Check if end date is in the past
    if (end < now) {
      errors.end = 'End date/time cannot be in the past';
    }

    // Check if end date is before start date
    if (end < start) {
      errors.end = 'End date/time must be after start date/time';
    }

    setDateErrors(errors);
    return !errors.start && !errors.end;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!validateDates()) {
      setTab('details');
      setIsLoading(false);
      return;
    }

    if (formData.eventType === 'Competition' && !formData.competitionType) {
      setTab('details');
      setError('Please select a competition type (Once Off or Series)');
      setIsLoading(false);
      return;
    }

    if (formData.eventType === 'Competition' && formData.competitionType === 'Series' && !formData.roundNumber) {
      setTab('details');
      setError('Round number is required for Series competitions');
      setIsLoading(false);
      return;
    }

    try {
      const promoterDetails = userSession.getUser()?.userTypeDetails?.find(d => d.typeId === 4);
      
      const eventData = {
        EventName: formData.eventName,
        EventType: formData.eventType,
        RoundNum: (formData.eventType === 'Competition' && formData.competitionType === 'Series') 
          ? (formData.roundNumber ? parseInt(formData.roundNumber) : null)
          : null,
        EventLink: formData.eventLink,
        StartDateTime: formData.startDateTime,
        EndDateTime: formData.endDateTime,
        OrganiserID: promoterDetails?.linkId,
        CountryID: parseInt(formData.country),
        StateID: formData.state,
        VenueID: formData.venue,
        LayoutID: formData.layout
      };
      
      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/events',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to create event');
      }

      await refreshEvents();
      if (onEventCreated) {
        onEventCreated();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create event. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'venue' && value === 'new') {
      setShowAddVenue(true);
      return;
    }
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Reset dependent fields when parent field changes
      if (name === 'country') {
        newData.state = '';
        newData.venue = '';
        newData.layout = '';
      } else if (name === 'state') {
        newData.venue = '';
        newData.layout = '';
      } else if (name === 'venue') {
        newData.layout = '';
      } else if (name === 'eventType') {
        // Reset competition fields when event type changes
        if (value !== 'Competition') {
          newData.competitionType = '';
          newData.roundNumber = '';
        }
      } else if (name === 'competitionType') {
        // Reset round number when competition type changes
        if (value !== 'Series') {
          newData.roundNumber = '';
        }
      }
      return newData;
    });
  };

  const handleVenueSelected = (venue) => {
    setFormData(prev => ({
      ...prev,
      venue: venue.VenueID.toString(),
      layout: '' // Reset layout when venue changes
    }));
  };

  const handleLayoutSelected = (layout) => {
    setFormData(prev => ({
      ...prev,
      layout: layout.LayoutID.toString()
    }));
  };

  const venueCountByState = (venues || []).reduce((counts, venue) => {
    const stateId = parseInt(venue.StateID, 10);
    counts[stateId] = (counts[stateId] || 0) + 1;
    return counts;
  }, {});

  const layoutCountByVenue = (layouts || []).reduce((counts, layout) => {
    const venueId = parseInt(layout.VenueID, 10);
    counts[venueId] = (counts[venueId] || 0) + 1;
    return counts;
  }, {});

  const stateOptions = countryStates.map((state) => ({
    id: state.StateID.toString(),
    label: state.StateNameShort,
    meta: optionMetaCount(venueCountByState[state.StateID], 'venue')
  }));

  const venueOptions = stateVenues.map((venue) => ({
    id: venue.id.toString(),
    label: venue.name,
    meta: optionMetaCount(layoutCountByVenue[venue.id], 'layout')
  }));

  const layoutOptions = venueLayouts.map((layout) => ({
    id: layout.id.toString(),
    label: layout.name
  }));

  return (
    <>
    <DashboardModal
      title="Add Event"
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      error={error}
      onRevealTab={setTab}
      tabs={<FormTabs tabs={TABS} active={tab} onChange={setTab} />}
    >
      <FormTabPanel id="details" active={tab}>
            <div>
              <label htmlFor="eventName" className="block text-sm font-medium text-gray-700">
                Event Name *
              </label>
              <input
                type="text"
                id="eventName"
                name="eventName"
                required
                value={formData.eventName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Event Type *
              </label>
              <FormSearchSelect
                name="eventType"
                value={formData.eventType}
                onChange={(value) => handleChange({ target: { name: 'eventType', value } })}
                required
                searchable={false}
                placeholder="Select event type..."
                options={eventTypes.map((type) => ({ id: type, label: type }))}
              />
            </div>

            {formData.eventType === 'Competition' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Competition Type *
                </label>
                <div className="flex space-x-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="competitionType"
                      value="Once Off"
                      checked={formData.competitionType === 'Once Off'}
                      onChange={handleChange}
                      className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Once Off</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="competitionType"
                      value="Series"
                      checked={formData.competitionType === 'Series'}
                      onChange={handleChange}
                      className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Series</span>
                  </label>
                </div>
              </div>
            )}

            {formData.eventType === 'Competition' && formData.competitionType === 'Series' && (
              <div>
                <label htmlFor="roundNumber" className="block text-sm font-medium text-gray-700">
                  Round Number *
                </label>
                <input
                  type="number"
                  id="roundNumber"
                  name="roundNumber"
                  required
                  min="1"
                  step="1"
                  value={formData.roundNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
                />
              </div>
            )}

            <div>
              <label htmlFor="eventLink" className="block text-sm font-medium text-gray-700">
                Event Link
              </label>
              <input
                type="url"
                id="eventLink"
                name="eventLink"
                value={formData.eventLink}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div>
              <label htmlFor="startDateTime" className="block text-sm font-medium text-gray-700">
                Start Date & Time *
              </label>
              <input
                type="datetime-local"
                id="startDateTime"
                name="startDateTime"
                required
                value={formData.startDateTime}
                onChange={handleChange}
                min={dateConstraints.min}
                max={dateConstraints.max}
                className={`mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500 ${
                  dateErrors.start ? 'border-red-300' : ''
                }`}
              />
              {dateErrors.start && (
                <p className="mt-1 text-sm text-red-600">{dateErrors.start}</p>
              )}
            </div>

            <div>
              <label htmlFor="endDateTime" className="block text-sm font-medium text-gray-700">
                End Date & Time *
              </label>
              <input
                type="datetime-local"
                id="endDateTime"
                name="endDateTime"
                required
                value={formData.endDateTime}
                onChange={handleChange}
                min={dateConstraints.min}
                max={dateConstraints.max}
                className={`mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500 ${
                  dateErrors.end ? 'border-red-300' : ''
                }`}
              />
              {dateErrors.end && (
                <p className="mt-1 text-sm text-red-600">{dateErrors.end}</p>
              )}
            </div>
      </FormTabPanel>

      <FormTabPanel id="location" active={tab}>
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                Country *
              </label>
              <select
                id="country"
                name="country"
                required
                value={formData.country}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              >
                <option value="0">Select a country</option>
                {countries?.map(country => (
                  <option key={country.CountryID} value={country.CountryID}>
                    {country.CountryName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                State *
              </label>
              <FormSearchSelect
                name="state"
                value={formData.state}
                onChange={(value) => handleChange({ target: { name: 'state', value } })}
                disabled={!formData.country}
                required
                placeholder="Search states..."
                options={stateOptions}
                resetKey={formData.country}
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Venue *
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddVenue(true)}
                  className="text-sm text-slate-900 hover:text-slate-700 font-semibold"
                >
                  + Add New Venue
                </button>
              </div>
              <FormSearchSelect
                name="venue"
                value={formData.venue}
                onChange={(value) => handleChange({ target: { name: 'venue', value } })}
                disabled={!formData.state}
                required
                placeholder="Search venues..."
                options={venueOptions}
                resetKey={formData.state}
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Layout *
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddLayout(true)}
                  className="text-sm text-slate-900 hover:text-slate-700 font-semibold"
                >
                  + Add New Layout
                </button>
              </div>
              <FormSearchSelect
                name="layout"
                value={formData.layout}
                onChange={(value) => handleChange({ target: { name: 'layout', value } })}
                disabled={!formData.venue}
                required
                placeholder="Search layouts..."
                options={layoutOptions}
                resetKey={formData.venue}
              />
            </div>

      </FormTabPanel>
    </DashboardModal>
      <AddVenue
        isOpen={showAddVenue}
        onClose={() => setShowAddVenue(false)}
        onVenueAdded={async () => {
          await refreshVenues();
          setShowAddVenue(false);
        }}
        onVenueSelected={handleVenueSelected}
        selectedStateId={formData.state}
      />
      <AddLayout
        isOpen={showAddLayout}
        onClose={() => setShowAddLayout(false)}
        onLayoutAdded={async () => {
          await refreshLayouts();
          setShowAddLayout(false);
        }}
        onLayoutSelected={handleLayoutSelected}
        venueId={formData.venue}
        venueName={selectedVenue?.VenueName}
      />
    </>
  );
} 